import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import InitialTradeForm from "./InitialTradeForm";
import SelectionStep from "./SelectionForm";
import SuccessStep from "./SuccessForm";
import { getSwapEmployeesV2, createSwapRequestV2 } from "../../../../redux/features/schedule/ScheduleApi";
import { setSwapNote, setFilterStartDate, setFilterEndDate } from "../../../../redux/features/schedule/ScheduleSlice";
import { IUser } from "../../../../redux/features/user/models/user";
import { RootState, useAppDispatch } from "../../../../redux/store";
import { CreateSwapRequest, ScheduleV2 } from "../../../../types";
import { useNavigate } from "react-router";
import { clearGlobalBackHandler, setGlobalBackHandler } from "../../../../hooks";
import { useNavigationHistory } from "../../../../hooks/useNavigationHistory";

const TradeShift = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { setSourcePath } = useNavigationHistory();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const fmShift = useSelector((state: RootState) => state.schedule.selectedScheduleForCannotWork) as ScheduleV2;
  const selectedEmployees = useSelector((state: RootState) => state.schedule.selectedSwapEmployees);
  const [note, setNote] = useState("");
  const [step, setStep] = useState(1);
  const filterStartDate = useSelector((state: RootState) => state.schedule.swapFilterStartDate);
  const filterEndDate = useSelector((state: RootState) => state.schedule.swapFilterEndDate);
  const User = useSelector((state: RootState) => state.user.user) as IUser;
  const actionConsecutiveDays = useSelector((state: RootState) => state.schedule.actionConsecutiveDays);
  const [name, setName] = useState<string>("");

  useEffect(() => {
    setStartDate(filterStartDate ? new Date(filterStartDate) : null);
    setEndDate(filterEndDate ? new Date(filterEndDate) : null);
  }, [filterStartDate, filterEndDate]);

  /**
   * Handle navigation to the previous step or back to schedule details
   */
  const handlePreviousStep = () => {
    if (step === 1) {
      navigate("/m-schedule-details");
    } else {
      setStep((prev) => prev - 1);
    }
  };

  // Set up navigation history and back handler
  useEffect(() => {
    // Set the source path for back navigation
    setSourcePath("/m-schedule-details");
    
    // Set up global back handler that checks current step when called
    setGlobalBackHandler(() => {
      handlePreviousStep();
      return true;
    });
    
    // Clean up when component unmounts
    return () => {
      clearGlobalBackHandler();
    };
  }, [navigate, setSourcePath, step]);

  const handleContinue = () => {
    dispatch(setSwapNote(note));
    setStep(2);
  };

  const handleStartDateChange = (date: Date | null) => {
    if (date) dispatch(setFilterStartDate(date.toISOString()));
    setStartDate(date);
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) dispatch(setFilterEndDate(date.toISOString()));
    setEndDate(date);
  };

  const handleSearch = () => {
    const defaultStartDate = new Date(new Date().getFullYear(), 0, 1);
    const defaultEndDate = new Date(new Date().getFullYear(), 11, 31);
    const payload = {
      request_type: 1,
      start_date: (startDate || defaultStartDate).toISOString(),
      end_date: (endDate || defaultEndDate).toISOString(),
      assigned_shift_code: fmShift.shift?.shift_code || "",
      department_code: (User as any).department_code || "",
      assigned_department_code: (User as any).department_code || "",
      group_code: [],
      name_search: name || "",
      days_in_swapping_block: actionConsecutiveDays,
    };
    if (startDate && endDate) dispatch(getSwapEmployeesV2(payload));
  };

  const handleNameSearchChange = (value: string) => {
    const payload = {
      request_type: 1,
      start_date: startDate ? startDate.toISOString() : new Date().toISOString(),
      end_date: endDate ? endDate.toISOString() : new Date(new Date().getFullYear(), 11, 31).toISOString(),
      assigned_shift_code: fmShift.shift?.shift_code || "",
      department_code: (User as any).department_code || "",
      assigned_department_code: (User as any).department_code || "",
      group_code: [],
      name_search: value.length > 3 ? value : "",
      days_in_swapping_block: actionConsecutiveDays,
    };
    if (value.length === 0 || value.length > 3) dispatch(getSwapEmployeesV2(payload));
  };

  const handleSuccess = () => {
    const empMap: { [key: string]: string[] } = selectedEmployees.reduce((map: any, employee: any) => {
      if (employee.emp_id != null) {
        if (!map[employee.emp_id]) map[employee.emp_id] = [];
        map[employee.emp_id].push(employee.schedule_id);
      }
      return map;
    }, {});

    const payload: CreateSwapRequest = {
      schedule_id: fmShift.schedules_id!,
      emp_schedule_map: empMap,
      days_in_swapping_block: actionConsecutiveDays,
      notes: note,
    };

    dispatch(createSwapRequestV2(payload));
    setStep(3);
  };

  return (
    <div>
      {step === 1 && <InitialTradeForm onContinue={handleContinue} setNote={setNote} />}
      {step === 2 && (
        <SelectionStep
          startDate={startDate}
          endDate={endDate}
          handleStartDateChange={handleStartDateChange}
          handleEndDateChange={handleEndDateChange}
          onSuccess={handleSuccess}
          handleNameSearch={setName}
          onSearch={handleSearch}
        />
      )}
      {step === 3 && <SuccessStep />}
    </div>
  );
};

export default TradeShift;
